<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <!-- Manual (card-present) charge: the staff member types the card here instead of
         texting a link. Rendered in place so we don't stack another drawer. -->
    <ManualCardChargeForm
      v-if="manualCardUuid"
      :paymentUuid="manualCardUuid"
      @completed="closeDrawer()" />

    <!-- Sent to a physical PayMe terminal — wait for the card there -->
    <div v-else-if="posCharge" class="flex-1 min-h-0 overflow-y-auto px-4">
      <PosChargeWaiting
        :paymentUuid="posCharge.uuid"
        :posId="posCharge.posId"
        :terminalName="posCharge.terminalName"
        :amount="posCharge.amount"
        @completed="closeDrawer()"
        @back="posCharge = undefined" />
    </div>

    <template v-else>
    <div class="flex flex-col w-full flex-1 min-h-0 gap-4 overflow-y-auto px-4 pb-12">
      <div
        v-if="hasPayme && invoiceAutomationOff"
        class="flex items-center justify-between p-3 bg-surface-50 rounded-lg border border-surface-200"
      >
        <div class="flex items-start gap-2.5">
          <i class="pi pi-receipt text-primary-600 text-lg mt-0.5"></i>
          <div class="flex flex-col gap-1">
            <span class="font-medium text-sm">שליחת חשבונית אוטומטית</span>
            <span class="text-xs text-surface-500">החשבונית לא תישלח ללקוח באופן אוטומטי. ניתן להפעיל את האוטומציה בעמוד האוטומציות.</span>
          </div>
        </div>
        <Button
          size="small"
          text
          icon="pi pi-arrow-left flex-none"
          iconPos="right"
          @click="goToInvoiceAutomation"
        />
      </div>

      <!-- Customer Selection -->
      <div class="flex flex-col gap-2">
        <span class="font-medium">בחירת לקוח</span>
        <SelectCustomer
          v-model="newPaymentRequest.customerId"
          @customer-selected="onCustomerSelected"
        />
      </div>

      <!-- Payment Method (for invoice) -->
      <div v-if="selectedAction === 'invoice'" class="flex flex-col w-full gap-2">
        <span class="subtitle-bold section">שיטת תשלום</span>
        <Select
          id="appointmentsStepGap"
          v-model="newPaymentRequest.paymentMethod"
          placeholder="איך בוצע התשלום?"
          :options="paymentMethods"
          optionLabel="label"
          optionValue="value"
        />
      </div>

      <!-- Amount -->
      <div class="flex flex-col w-full gap-2">
        <span class="subtitle-bold section">{{ selectedAction === 'payment' ? 'סכום לתשלום' : 'סכום ששולם' }}</span>
        <input-number
          v-model="newPaymentRequest.price"
          :maxFractionDigits="0"
          placeholder="מהו הסכום לתשלום?"
          mode="currency"
          currency="ILS"
          locale="he-IL"
        />
      </div>

      <!-- Customer credit (יתרה) -->
      <div v-if="availableCredit > 0" class="flex flex-col w-full gap-1">
        <settings-toggle
          :status="!!newPaymentRequest.useCustomerBalance"
          :title="`קיזוז מיתרת הלקוח (${formatIls(availableCredit)} זמינים)`"
          @value-changed="(val: boolean) => { newPaymentRequest.useCustomerBalance = val; }" />
        <span v-if="newPaymentRequest.useCustomerBalance && newPaymentRequest.price" class="text-xs text-surface-500">
          {{ selectedAction === 'payment' ? 'לתשלום' : 'בחשבונית' }}: {{ formatIls(Math.max(0, newPaymentRequest.price - availableCredit)) }}
        </span>
      </div>

      <!-- Purpose -->
      <div class="flex flex-col w-full gap-2">
        <span class="subtitle-bold section">מטרת התשלום</span>
        <input-text
          v-model="newPaymentRequest.note"
          placeholder="לדוגמא, תשלום על שירות"
        />
      </div>

      <!-- Email -->
      <div class="flex flex-col w-full gap-2 mb-4" v-if="hasPayme">
        <span class="subtitle-bold section">כתובת מייל</span>
        <input-text
          v-model="newPaymentRequest.email"
          placeholder="name@example.com"
        />
      </div>

      <!-- Payment Type Selection (for link action with PayMe) -->
      <div v-if="selectedAction === 'payment' && hasPayme" class="flex flex-col w-full gap-2">
        <span class="subtitle-bold section">אופן התשלום</span>
        <div :class="posTerminals.length ? 'grid grid-cols-2 gap-2' : 'flex gap-2'">
          <div
            class="flex-1 flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all"
            :class="paymentType === 'link' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
            @click="paymentType = 'link'"
          >
            <i class="pi pi-send text-2xl mb-2"></i>
            <span class="font-medium text-sm">שליחת קישור</span>
            <span class="text-xs text-gray-500 text-center">הלקוח ישלם דרך הקישור</span>
          </div>
          <div
            class="flex-1 flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all"
            :class="paymentType === 'manual' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
            @click="paymentType = 'manual'"
          >
            <i class="pi pi-credit-card text-2xl mb-2"></i>
            <span class="font-medium text-sm">חיוב ידני</span>
            <span class="text-xs text-gray-500 text-center">הזנת פרטי הכרטיס כאן</span>
          </div>
          <div
            v-if="availablePaymentMethods?.bit"
            class="flex-1 flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all"
            :class="paymentType === 'bit' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
            @click="paymentType = 'bit'"
          >
            <i class="pi pi-mobile text-2xl mb-2"></i>
            <span class="font-medium text-sm">תשלום בביט</span>
            <span class="text-xs text-gray-500 text-center">בקשת תשלום בביט תשלח בהודעה אל הלקוח</span>
          </div>
          <div
            v-if="posTerminals.length"
            class="flex-1 flex flex-col items-center p-4 border-2 rounded-xl cursor-pointer transition-all"
            :class="paymentType === 'pos' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
            @click="selectPos"
          >
            <i class="pi pi-credit-card text-2xl mb-2"></i>
            <span class="font-medium text-sm">חיוב במסוף</span>
            <span class="text-xs text-gray-500 text-center">הלקוח מעביר כרטיס במסוף אשראי</span>
          </div>
        </div>
        <!-- More than one terminal: which one -->
        <div v-if="paymentType === 'pos' && posTerminals.length > 1" class="flex gap-2 flex-wrap">
          <button
            v-for="t in posTerminals"
            :key="t.posId"
            type="button"
            class="h-9 px-3.5 rounded-full border text-sm"
            :class="selectedPosId === t.posId ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium' : 'border-gray-200 text-gray-700'"
            @click="selectedPosId = t.posId"
          >{{ terminalLabel(t) }}</button>
        </div>
      </div>
    </div>

    <div class="bg-white shadow-[0_-8px_16px_-4px_rgba(0,0,0,0.05)] flex-shrink-0 px-4">
      <drawer-footer
        :approveDisable="!validateFinished"
        :approveLoading="loadingCta"
        :approveLabel="getApproveLabel"
        @approve-click="createPayment"
        @cancel-click="newPaymentModalVisible=false"
      />
    </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import {
  ICustomer,
  IOtherPaymentRequest,
  IPaymeSaleData,
  PaymentMethod,
  PaymentProvider,
  PaymeSettings,
  type IPaymePosTerminal
} from '@torim-app/types';
import { PaymentsApi, CommunicationApi, CustomersApi } from '@app/api/admin';
import Select from 'primevue/select';
import PosChargeWaiting from '@app/components/admin/modals/PosChargeWaiting.vue';
import { computed, onMounted, ref } from 'vue';
import { useAdminClientsStore, useAdminStore } from '@app/stores';
import { useToast } from 'vue-toastification';
import { useDrawer } from '@torim-app/shared-components';
import ManualCardChargeForm from '@app/components/admin/components/ManualCardChargeForm.vue';
import { trackEvent } from '@app/composables/posthog';
import { useRouter } from 'vue-router';
import { AdminRouteNames } from '@app/router/route-names';
import { AutomationTypeEnum } from '@torim-app/types';
import type { IAutomationConfig } from '@torim-app/types';

const props = defineProps({
  selectedAction: {
    required: true,
    type: String as () => 'invoice' | 'payment'
  }
});

const adminStore = useAdminStore();
const adminClientsStore = useAdminClientsStore();
const toast = useToast();
const router = useRouter();
const { closeDrawer } = useDrawer();

// Default to false so the banner never flashes for merchants who already have
// the automation enabled. We flip it on only once the API confirms it's off.
const invoiceAutomationOff = ref(false);

onMounted(async () => {
  try {
    const configs: IAutomationConfig[] = await CommunicationApi.fetchAutomationConfigs();
    const cfg = configs.find(c => c.type === AutomationTypeEnum.InvoiceReceipt);
    if (!cfg?.enabled) invoiceAutomationOff.value = true;
  } catch {
    // Stay hidden on failure — better to miss the nudge than to flash a stale banner.
  }
});

const goToInvoiceAutomation = () => {
  closeDrawer();
  router.push({ name: AdminRouteNames.Communication, query: { tab: '0' } });
};

const newPaymentModalVisible = ref(false);
const loadingCta = ref(false);
const newPaymentRequest = ref<IOtherPaymentRequest>({} as IOtherPaymentRequest);
// 'link' = text the customer a payment link, 'manual' = type the card here (card-present),
// 'bit' = send a Bit request. All three are credit-side; only the delivery differs.
const paymentType = ref<'link' | 'manual' | 'bit' | 'pos'>('link');
// Set once a request was sent to a PayMe terminal — the drawer then waits for the card.
const posCharge = ref<{ uuid: string; posId: string; terminalName?: string; amount?: number }>();
const selectedPosId = ref<string>();
// Set once a manual request exists — the drawer then shows the hosted card form.
const manualCardUuid = ref<string>();
const selectedCustomer = ref<Pick<ICustomer, 'id' | 'fullName' | 'tags' | 'loyalty' | 'smsOptedOut' | 'phoneNumber'>>();

const validateFinished = computed(() => {
  return newPaymentRequest.value.price && newPaymentRequest.value.customerId;
});

// The selected customer's credit (יתרה), which can be taken off this payment.
const availableCredit = ref(0);
const formatIls = (n: number) => '₪' + n.toLocaleString('he-IL', { maximumFractionDigits: 2 });

const onCustomerSelected = async (selectedCustomerObj: any) => {
  selectedCustomer.value = selectedCustomerObj;
  newPaymentRequest.value.customerId = selectedCustomerObj.id;
  newPaymentRequest.value.email = selectedCustomerObj.email;
  newPaymentRequest.value.useCustomerBalance = false;
  availableCredit.value = 0;
  try {
    const { balance } = await CustomersApi.getBalance(selectedCustomerObj.id);
    if (newPaymentRequest.value.customerId === selectedCustomerObj.id) availableCredit.value = Math.max(0, balance);
  } catch {
    // No credit option on failure — the payment itself is unaffected.
  }
};

const getApproveLabel = computed(() => {
  if (props.selectedAction === 'invoice') return 'הפקת חשבונית';
  if (!hasPayme.value) return 'שליחת בקשת תשלום';
  if (paymentType.value === 'bit') return 'שליחת בקשה בביט';
  if (paymentType.value === 'manual') return 'המשך להזנת כרטיס';
  if (paymentType.value === 'pos') return 'שליחה למסוף';
  return 'שליחת בקשת תשלום';
});

const paymentMethods = computed<Array<{ label: string; value: PaymentMethod }>>(() => {
  const options = [
    { label: 'מזומן', value: PaymentMethod.Cash },
    { label: 'ביט', value: PaymentMethod.Bit },
    { label: 'העברה בנקאית', value: PaymentMethod.BankTransfer },
    { label: 'פייבוקס', value: PaymentMethod.PayBox }
  ];
  if (hasPayme.value) return options.filter(o => o.value === PaymentMethod.Cash || o.value === PaymentMethod.BankTransfer);
  return options;
});

const hasPayme = computed(() => {
  return adminStore.businessSiteData?.paymentIntegration?.paymentConfig?.provider === PaymentProvider.PAYME;
});

// Physical PayMe card terminals, set by hand on the business's PayMe settings.
const posTerminals = computed<IPaymePosTerminal[]>(() => hasPayme.value
  ? ((adminStore.businessSiteData?.paymentIntegration?.paymentConfig?.settings as PaymeSettings | undefined)?.posTerminals ?? [])
  : []);
const terminalLabel = (t: IPaymePosTerminal) => t.name || `מסוף ${t.posId}`;
const selectPos = () => {
  paymentType.value = 'pos';
  if (!posTerminals.value.some(t => t.posId === selectedPosId.value)) selectedPosId.value = posTerminals.value[0]?.posId;
};

const availablePaymentMethods = computed(() => {
  if(!hasPayme.value) return {} as IPaymeSaleData['paymentMethods'];
  return (adminStore.businessSiteData?.paymentIntegration?.paymentConfig?.settings as PaymeSettings)?.paymentMethods
});

const createPayment = async () => {
  trackEvent('payment_request_created', { payment_type: paymentType.value });
  loadingCta.value = true;
  try {
    if (!newPaymentRequest.value.customerId || !newPaymentRequest.value.price) return;
    const isManual = props.selectedAction === 'payment' && hasPayme.value && paymentType.value === 'manual';
    const terminal = props.selectedAction === 'payment' && paymentType.value === 'pos'
      ? posTerminals.value.find(t => t.posId === selectedPosId.value)
      : undefined;
    if (props.selectedAction === 'payment') {
      newPaymentRequest.value.paymentMethod = paymentType.value === 'bit' ? PaymentMethod.Bit : PaymentMethod.Credit;
      // Manual / terminal charge: create the request, but don't text the customer a link
      newPaymentRequest.value.dontSendLink = isManual || !!terminal;
    }

    const { uuid } = await PaymentsApi.sendGeneralPaymentLink(newPaymentRequest.value);
    if (terminal) {
      if (!uuid) {
        toast.error('לא הצלחנו ליצור את החיוב');
        return;
      }
      const result = await PaymentsApi.startPosCharge({ paymentRequestUuid: uuid, posId: terminal.posId });
      trackEvent('pos_charge_sent', { payFor: 'general', ok: result.success });
      if (!result.success) {
        toast.error(result.error || 'לא הצלחנו לשלוח את החיוב למסוף');
        return;
      }
      posCharge.value = { uuid, posId: terminal.posId, terminalName: terminalLabel(terminal), amount: newPaymentRequest.value.price };
      return;
    }
    if (isManual) {
      if (!uuid) {
        toast.error('לא הצלחנו לפתוח את טופס החיוב');
        return;
      }
      manualCardUuid.value = uuid;
      return;
    }

    toast.success('הבקשה נשלחה בהצלחה');
    newPaymentModalVisible.value = false;
    newPaymentRequest.value = <IOtherPaymentRequest>{};
    closeDrawer();
  } catch (e) {
    console.log(e);
    toast.error('לא הצלחנו לשלוח את הבקשה, אנא נסו שוב');
  } finally {
    loadingCta.value = false;
  }
};
</script>


<style scoped lang="scss">

</style>
